<script setup lang="ts">
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'

/**
 * Tenant site dynamic page — /{slug} on a tenant host renders a published
 * block page. Static platform routes (pricing, demos, …) take precedence
 * automatically; on the platform domain this route 404s.
 */
definePageMeta({ layout: false, middleware: ['tenant'] })

const route = useRoute()
const tenant = useTenant()

if (tenant.tenantMode.value === 'platform') {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const activeTenant = computed(() => tenant.tenant.value)
const repo = usePublicSiteRepository()
const slug = String(route.params.pageSlug)

const { data: response, error } = await useAsyncData(
  `site-page-${slug}`,
  () => repo.getPage(slug)
)

if (error.value || !response.value?.data) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

const page = computed(() => response.value!.data)

useSeoMeta({
  title: () => page.value.seo.meta_title || `${page.value.title} — ${activeTenant.value?.name ?? ''}`,
  description: () => page.value.seo.meta_description || page.value.excerpt || undefined,
  ogTitle: () => page.value.seo.meta_title || page.value.title,
  ogDescription: () => page.value.seo.meta_description || page.value.excerpt || undefined,
  ogImage: () => page.value.seo.og_image_url || page.value.cover_image_url || undefined,
  ogType: 'article'
})
</script>

<template>
  <SiteShell
    v-if="activeTenant"
    :tenant="activeTenant"
  >
    <article class="py-10 sm:py-14">
      <header class="mx-auto max-w-5xl px-6 pb-2">
        <h1 class="site-display text-4xl tracking-tight sm:text-5xl">
          {{ page.title }}
        </h1>
      </header>
      <SiteBlockRenderer :blocks="page.blocks" />
    </article>
  </SiteShell>
</template>
